import type { Tenant } from '@pflegenavi/frontend/tenant';
import type { AuthenticationContext } from '@pflegenavi/frontend/authentication';
import type { Api } from '@pflegenavi/shared-frontend/platform';
import {
  get,
  getPhoenixApiTenantUrl,
  modify,
} from '@pflegenavi/shared-frontend/platform';
import type {
  CashListSegment,
  CashListStorageType,
  CashListAccountType,
  Coin,
  CashManagementTransactionType,
  CashTransactionGroupType,
  TransactionType,
  TransactionSourceType,
  TransactionInitiatedBy,
} from '@pflegenavi/shared/api';
import { addMissingFactorsAndSort } from '../cash-management';

export interface CreateCashListTransactionGroupParams {
  body: {
    data: {
      coins?: Coin[];
      notes?: string;
      bank_account_amount?: number;
      type: 'resident deposit';
      resident_id: string;
    };
  };
  params: {
    cashListId: string;
  };
}

export interface GetCashListTransactionGroupParams {
  params: {
    page: number;
    pageSize: number;
    cashListIds?: string[];
    pliantPaymentEmployeeId?: string;
    mismatch?: boolean;
    groupTypes?: GroupType[];
    orderBy?: 'update_date' | 'initial_pliant_transaction_created_at';
    orderDirection?: 'asc' | 'desc';
    transactionIds?: string[];
    filterIncompleteOnly?: boolean;
    filterOpenReceipts?: boolean;
    filterMismatchesAndMultipleOpenItems?: boolean;
  };
}

export interface CashListParams {
  params?: {
    page?: number;
    page_size?: number;
    nursing_home_id?: string;
  };
}

export interface CashListConfigurationParams {
  params: {
    nursing_home_id: string;
  };
}

export interface CashListConfiguration {
  nursing_home_id: string;
  payout_target_cash_list_id: string;
  service_provider_payment_target_cash_list_id: string;
  payin_target_cash_list_id: string;
  pliant_reference_cash_list_id: string;
}

export interface CashList {
  id: string;
  name: string;
  account_type: CashListAccountType;
  storage_type: CashListStorageType;
  total_cash: number;
  bank_account_amount: number;
  coins: Coin[];
  currency_type: string;
  segment: CashListSegment;
  supports_cash: boolean;
  supports_bank_account: boolean;
  created_at: string;
  updated_at: string;
}

export interface GetCashListsResponse {
  data: CashList[];
  meta?: {
    current_page: number;
    next_page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
  };
}

export interface GetCashListConfigurationResponse {
  data: CashListConfiguration;
}

type GroupType =
  | 'ReceiptWithdrawal'
  | 'ResidentDeposit'
  | 'ResidentWithdrawal'
  | 'Transfer'
  | 'Adjustment'
  | 'Deposit';

/**
 * Represents a cash transaction group list entry with aggregated data.
 * This is a materialized view of the cash list transaction group data.
 */
export interface CashTransactionGroupListEntry {
  id: string;
  group_type: string;
  cash_changed: number;
  separate_fee_amount: number;
  immediate_fee_amount: number;
  transaction_amount: number;
  amount_linked: number;
  separate_fee_amount_linked: number;
  immediate_fee_amount_linked: number;
  linking_difference: number;
  update_date: string;
  receipt_count: number;
  settled_receipt_count: number;
  open_receipt_count: number;
  open_receipt_batch_count: number;
  submitted_receipt_batch_count: number;
  transaction_count: number;
  payment_intent_count: number;
  payout_count: number;
  nursing_home_payout_count: number;
  cash_list_transaction_count: number;
  transfer_count: number;
  receipt_amount: number;
  settled_receipt_amount: number;
  settled_receipt_additional_fee_amount: number;
  settled_receipt_included_fee_amount: number;
  open_receipt_amount: number;
  open_receipt_additional_fee_amount: number;
  open_receipt_included_fee_amount: number;
  payment_intent_amount: number;
  payout_amount: number;
  cash_list_transaction_amount: number;
  receipt_ids: string[];
  settled_receipt_ids: string[];
  open_receipt_ids: string[];
  pliant_transaction_amount?: number;
  reversed?: boolean;
  manual_transaction_creation_allowed?: boolean;
}

/**
 * Represents a link between a cash list transaction group and other entities
 * (receipts, transactions, etc.)
 */
export interface CashListTransactionGroupLink {
  receipt?: {
    id: string;
    amount: number;
    notes?: string;
    receipt_date: string;
    service_provider_id?: string;
    receipt_type_id?: string;
    resident_id?: string;
    resident?: {
      id: string;
      first_name?: string;
      last_name?: string;
    };
    submission_date?: string;
    embargo_date?: string;
    submitted_by?: string;
  };
  transaction?: {
    id: string;
    amount: number;
    additional_fee_amount: number;
    included_fee_amount: number;
    additional_fees_diverted_to_pflegenavi: boolean;
    notes?: string;
    type: string;
    source_type: string;
    initiated_by: string;
    resident?: {
      id: string;
      first_name?: string;
      last_name?: string;
    };
    creation_date: string;
    creator_id?: string;
    creator_employee?: {
      id: string;
      first_name?: string;
      last_name?: string;
    };
  };
  creation_datetime?: string;
  external_id?: string;
  external_type?: string;
}

export interface CashListTransactionGroupSingle {
  id: string;
  group_type: GroupType;
  cash_list_id: string;
  employee_id: string;
  mismatch_confirmation_timestamp?: string;
  mismatch_confirmation_employee_id?: string;
  notes?: string;
  reversed: boolean;
  manual_transaction_creation_allowed: boolean;
  cash_transfers: number;
  cash_list_transactions: Array<{
    id: string;
    amount: number;
    separate_fee_amount: number;
    separate_fee_percentage: string | null;
    immediate_fee_amount: number;
    immediate_fee_tracked: boolean;
    cash_list_id: string;
    type: string;
    employee_id: string;
    employee?: {
      id: string;
      first_name?: string;
      last_name?: string;
    };
    coin_change?: Coin[];
    notes?: string;
    bank_account_amount_change?: number;
    pliant_transaction?: {
      pliant_organization_id?: string;
      pliant_card_account_id?: string;
      pliant_cardholder_id?: string;
      pliant_cardholder?: {
        pliant_cardholder_id: string;
        pliant_organization_id: string;
        pliant_cardholder_status: string;
        employee?: {
          id: string;
          first_name: string;
          last_name: string;
          email: string;
        };
      };
      pliant_card_id?: string;
      pliant_status?: string;
      pliant_type?: string;
      pliant_category?: string;
      billing_amount: number;
      created_at?: Date;
      authorized_at?: Date;
      confirmed_at?: Date;
      booked_at?: Date;
      reversed_at?: Date | null;
      inserted_at?: string;
      updated_at?: string;
      merchant_base_data?: {
        id?: string;
        title?: string;
        first_name?: string;
        last_name?: string;
        company?: string;
        birth_date?: string;
        address?: string;
        house_number?: string;
        door?: string;
        zip_code?: string;
        city?: string;
        state?: string;
        phone?: string;
        fax?: string;
        email?: string;
        website?: string;
        country?: unknown;
        nationality?: unknown;
        country_of_residence?: unknown;
      } | null;
      pliant_merchant_id?: string | null;
      merchant_name?: string;
      mcc?: string;
      merchant_terminal_id?: string;
    };
    inserted_at: string;
    event_datetime: string;
    full_reversal: boolean;
  }>;
  cash_transaction_group_list_entry: CashTransactionGroupListEntry;
  cash_list_transaction_group_links?: CashListTransactionGroupLink[];
}

export interface CashListTransactionGroup {
  id: string;
  group_type: GroupType;
  employee_id: string;
  mismatch_confirmation_timestamp: string;
  pliant_transaction_amount: number;
  cash_changed: number;
  transaction_amount: number;
  amount_linked: number;
  linking_difference: number;
  update_date: Date;
  pliant_transaction_types: string[];
  initial_pliant_transaction_created_at?: Date;
  receipt_ids?: string[];
  settled_receipt_ids?: string[];
  open_receipt_ids?: string[];
  open_receipt_batch_ids?: string[];
  // Count fields
  receipt_count: number;
  settled_receipt_count: number;
  open_receipt_count: number;
  // Receipt amount fields
  receipt_amount: number;
  settled_receipt_amount: number;
  settled_receipt_additional_fee_amount: number;
  settled_receipt_included_fee_amount: number;
  open_receipt_amount: number;
  open_receipt_additional_fee_amount: number;
  open_receipt_included_fee_amount: number;
  // Fee amount fields
  immediate_fee_amount: number;
  separate_fee_amount: number;
  separate_fee_amount_linked: number;
  immediate_fee_amount_linked: number;
  cash_list_transaction_immediate_fee_amount: number;
  cash_list_transaction_separate_fee_amount: number;
}

export interface GetCashListTransactionGroupResponse {
  cash_transaction_group_list: CashListTransactionGroup[];
  meta: {
    current_page: number;
    previous_page: number;
    next_page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
    has_next_page: boolean;
    has_previous_page: boolean;
  };
}

export interface CreateCashListTransactionGroupFromPliantParams {
  body: {
    nursing_home_id: string;
    pliant_transaction_id: string;
  };
}

export interface ReassignPliantTransactionParams {
  params: {
    cash_list_id: string;
  };
  body: {
    nursing_home_id: string;
    pliant_transaction_id: string;
  };
}

export interface TransferCashListParams {
  params: {
    fromCashListId: string;
    toCashListId: string;
  };
  body: {
    from: {
      coins?: Coin[];
      bankAccountAmount?: number;
      note?: string;
      pliant_transaction_id?: string;
    };
    to: {
      coins?: Coin[];
      bankAccountAmount?: number;
      note?: string;
    };
  };
}

/**
 * Basic cash list transaction data returned from the transfer endpoint.
 * This is a simplified version without employee details or pliant transaction data.
 */
interface TransferCashListTransaction {
  id: string;
  amount: number;
  type: string;
  cash_list_id: string;
  employee_id: string;
  coin_change?: Coin[];
  bank_account_amount_change?: number;
  notes?: string;
  immediate_fee_amount: number;
  immediate_fee_tracked: boolean;
  separate_fee_amount: number;
  separate_fee_percentage: string | null;
  inserted_at: string;
  event_datetime: string;
  full_reversal: boolean;
}

/**
 * Transaction group data returned from the transfer endpoint.
 * Uses the data() function which returns a subset of fields compared to get_group_data().
 */
interface TransferTransactionGroup {
  id: string;
  group_type: string;
  employee_id: string;
  notes?: string;
  mismatch_confirmation_timestamp?: string;
  mismatch_confirmation_employee_id?: string;
  cash_list_transactions: TransferCashListTransaction[];
  cash_transaction_group_list_entry: CashTransactionGroupListEntry | null;
  cash_list_transaction_group_links: CashListTransactionGroupLink[];
}

/**
 * Response from the Phoenix transfer endpoint.
 * Returns the full transaction group data for both sides of the transfer.
 */
export interface TransferCashListResponse {
  from_group: TransferTransactionGroup;
  to_group: TransferTransactionGroup;
}

export interface GetCashListTransactionsParams {
  params: {
    cash_list_id: string;
    page?: number;
    page_size?: number;
    date_from?: Date;
    date_to?: Date;
  };
}

export interface CashListTransaction {
  id: string;
  cash_list_id: string;
  type: string;
  status: string;
  amount: number;
  separate_fee_amount: number;
  separate_fee_percentage?: string;
  immediate_fee_amount: number;
  immediate_fee_tracked: boolean;
  notes?: string;
  coin_change: Coin[];
  bank_account_amount_change: number;
  group_id?: string;
  full_reversal: boolean;
  inserted_at: string;
  event_datetime: string;
  employee_id?: string;
  employee?: {
    id: string;
    first_name: string;
    last_name: string;
  };
  group?: {
    id: string;
    group_type: string;
  };
  pliant_transaction_id?: string;
  pliant_transaction?: {
    pliant_transaction_id: string;
    pliant_type: string;
    pliant_status: string;
    billing_amount: number;
  };
  balance_before: number;
  balance_after: number;
  related_residents: Array<{
    id: string;
    firstname: string;
    lastname: string;
  }>;
}

export interface GetCashListTransactionsResponse {
  data: CashListTransaction[];
  meta: {
    current_page: number;
    total_pages: number;
    page_size: number;
    total_count: number;
  };
}

export interface TransformedCashTransactionGroup {
  id: string;
  type: CashTransactionGroupType;
  updateDate: Date;
  cashListId: string;
  cashListTransactionCount: number;
  transactionLinkCount: number;
  receiptLinkCount: number;
  paymentIntentLinkCount: number;
  payoutLinkCount: number;
  linkCount: number;
  openReceiptBatchCount: number;
  submittedReceiptBatchCount: number;
  cashChanged: number;
  separateFeeAmount: number;
  immediateFeeAmount: number;
  amountLinked: number;
  separateFeeAmountLinked: number;
  immediateFeeAmountLinked: number;
  cashTransfers: number;
  reversed: boolean;
  manualTransactionCreationAllowed: boolean;
  mismatchConfirmationDate?: Date;
  pliantTransactionAmount?: number;
  transactions?: Array<{
    id: string;
    amount: number;
    additionalFeeAmount: number;
    includedFeeAmount: number;
    additionalFeesDivertedToPflegenavi: boolean;
    type: TransactionType;
    sourceType: TransactionSourceType;
    receiptType?: string;
    resident?: {
      id: string;
      firstName?: string;
      lastName?: string;
    };
    initiator?: {
      id: string;
      firstName?: string;
      lastName?: string;
      fullName: string;
    };
    notes?: string;
    date: Date;
    initiatedBy: TransactionInitiatedBy;
    additionalData: Record<string, string>;
  }>;
  cashListTransactions?: Array<{
    id: string;
    cashListId: string;
    type: CashManagementTransactionType;
    resident?: {
      id: string;
      firstName: string;
      lastName: string;
    };
    amount: number;
    separateFeeAmount: number;
    separateFeePercentage: string | null;
    immediateFeeAmount: number;
    immediateFeeTracked: boolean;
    date: Date;
    eventDate: Date;
    employee?: {
      id: string;
      firstName?: string;
      lastName?: string;
    };
    status: 'confirmed';
    notes: string;
    coinChange?: Coin[];
    bankAccountAmountChange?: number;
    fullReversal: boolean;
  }>;
}

export interface ICashManagementPhoenixApi extends Api {
  createCashListTransactionGroup(
    dataIn: CreateCashListTransactionGroupParams
  ): Promise<{
    id: string;
  }>;

  createFromPliant(
    dataIn: CreateCashListTransactionGroupFromPliantParams
  ): Promise<{ cash_list_transaction_group: CashListTransactionGroupSingle }>;

  reassignPliantTransaction(
    dataIn: ReassignPliantTransactionParams
  ): Promise<unknown>;
  getCashListTransactionGroup(
    dataIn: GetCashListTransactionGroupParams
  ): Promise<GetCashListTransactionGroupResponse>;

  getCashTransactionGroup(dataIn: {
    params: { groupId: string };
  }): Promise<{ cash_list_transaction_group: CashListTransactionGroupSingle }>;

  transfer(dataIn: TransferCashListParams): Promise<TransferCashListResponse>;

  getCashLists(dataIn?: CashListParams): Promise<GetCashListsResponse>;

  getCashListConfiguration(
    dataIn: CashListConfigurationParams
  ): Promise<GetCashListConfigurationResponse>;

  getCashListTransactions(
    dataIn: GetCashListTransactionsParams
  ): Promise<GetCashListTransactionsResponse>;
}

export class CashManagementPhoenixApi implements ICashManagementPhoenixApi {
  public createCashListTransactionGroup: ICashManagementPhoenixApi['createCashListTransactionGroup'];
  public getCashListTransactionGroup: ICashManagementPhoenixApi['getCashListTransactionGroup'];
  public getCashTransactionGroup: ICashManagementPhoenixApi['getCashTransactionGroup'];
  public createFromPliant: ICashManagementPhoenixApi['createFromPliant'];
  public transfer: ICashManagementPhoenixApi['transfer'];
  public getCashLists: ICashManagementPhoenixApi['getCashLists'];
  public getCashListConfiguration: ICashManagementPhoenixApi['getCashListConfiguration'];
  public reassignPliantTransaction: ICashManagementPhoenixApi['reassignPliantTransaction'];
  public getCashListTransactions: ICashManagementPhoenixApi['getCashListTransactions'];

  constructor(
    tenantId: Tenant,
    public authContext: AuthenticationContext,
    apiUrl?: string
  ) {
    const baseUrl = getPhoenixApiTenantUrl(tenantId, apiUrl);

    this.createCashListTransactionGroup = modify({
      authContext,
      method: 'POST',
      transformBody: ({ data }) => {
        if (data.coins) {
          return {
            data: {
              ...data,
              coins: addMissingFactorsAndSort(data.coins).map(
                (coin) => coin.amount
              ),
            },
          };
        } else {
          return { data };
        }
      },
      url: ({ cashListId }) =>
        `${baseUrl}/cash-list/${cashListId}/cash-transaction-group`,
    });

    this.createFromPliant = modify({
      authContext,
      method: 'POST',
      url: () => `${baseUrl}/cash-list/pliant-cash-transaction`,
    });

    this.reassignPliantTransaction = modify({
      authContext,
      method: 'POST',
      transformBody: ({ nursing_home_id, pliant_transaction_id }) => ({
        nursing_home_id,
        pliant_transaction_id,
      }),
      url: ({ cash_list_id }) =>
        `${baseUrl}/cash-list/${cash_list_id}/reassign-pliant-transaction`,
    });

    this.transfer = modify({
      authContext,
      method: 'POST',
      transformBody: ({ from, to }) => {
        const transformCoins = (coins?: Coin[]) => {
          if (!coins || coins.length === 0) {
            return undefined;
          }
          return coins.map((coin) => ({
            factor: coin.factor,
            amount: coin.amount,
          }));
        };

        return {
          from: {
            ...from,
            coins: transformCoins(from.coins),
          },
          to: {
            ...to,
            coins: transformCoins(to.coins),
          },
        };
      },
      url: ({ fromCashListId, toCashListId }) =>
        `${baseUrl}/cash-list/${fromCashListId}/transfer/${toCashListId}`,
    });

    this.getCashListTransactionGroup = get({
      authContext,
      url: (params) => {
        const query = new URLSearchParams();

        query.append('page', params.page.toString());
        query.append('page_size', params.pageSize.toString());

        let i = 0;
        if (params.cashListIds) {
          query.append(`filters[${i}][field]`, 'cash_list_id');
          query.append(`filters[${i}][op]`, 'in');
          for (const cashListId of params.cashListIds) {
            query.append(`filters[${i}][value][]`, cashListId);
          }
          i++;
        }

        if (params.groupTypes) {
          query.append(`filters[${i}][field]`, 'group_type');
          query.append(`filters[${i}][op]`, 'in');
          for (const groupType of params.groupTypes) {
            query.append(`filters[${i}][value][]`, groupType);
          }
          i++;
        }

        if (params.pliantPaymentEmployeeId) {
          query.append(`filters[${i}][field]`, 'pliant_payment_employee_ids');
          query.append(`filters[${i}][op]`, '==');
          query.append(
            `filters[${i}][value][]`,
            params.pliantPaymentEmployeeId
          );
          i++;
        }

        if (params.mismatch === true) {
          query.append(
            `filters[${i}][field]`,
            'mismatch_confirmation_timestamp'
          );
          query.append(`filters[${i}][op]`, 'empty');
          query.append(`filters[${i}][value]`, 'true');
          i++;
          query.append(`filters[${i}][field]`, 'linking_difference');
          query.append(`filters[${i}][op]`, '!=');
          query.append(`filters[${i}][value]`, '0');
          i++;
        }

        if (params.transactionIds) {
          query.append(`filters[${i}][field]`, 'transaction_ids');
          query.append(`filters[${i}][op]`, 'in');
          for (const transactionId of params.transactionIds) {
            query.append(`filters[${i}][value][]`, transactionId);
          }
          i++;
        }

        if (params.orderBy) {
          query.append(`order_by[]`, params.orderBy);
          query.append(`order_directions[]`, params.orderDirection ?? 'desc');
        }

        if (params.filterIncompleteOnly !== undefined) {
          query.append(
            'filter_incomplete_only',
            params.filterIncompleteOnly.toString()
          );
        }

        if (params.filterOpenReceipts !== undefined) {
          query.append(
            'filter_open_receipts',
            params.filterOpenReceipts.toString()
          );
        }

        if (params.filterMismatchesAndMultipleOpenItems !== undefined) {
          query.append(
            'filter_mismatches_and_multiple_open_items',
            params.filterMismatchesAndMultipleOpenItems.toString()
          );
        }

        return `${baseUrl}/cash-list/cash-transaction-group-list?${query.toString()}`;
      },
    });

    this.getCashLists = get({
      authContext,
      url: (params) => {
        const query = new URLSearchParams();
        let i = 0;

        if (params?.page !== undefined) {
          query.append('page', params.page.toString());
        }

        if (params?.page_size !== undefined) {
          query.append('page_size', params.page_size.toString());
        }

        if (params?.nursing_home_id !== undefined) {
          query.append(`filters[${i}][field]`, 'nursing_home_id');
          query.append(`filters[${i}][op]`, '==');
          query.append(`filters[${i}][value]`, params.nursing_home_id);
          i++;
        }

        const queryString = query.toString();
        return `${baseUrl}/cash-list?${queryString}`;
      },
    });

    this.getCashListConfiguration = get({
      authContext,
      url: ({ nursing_home_id }) =>
        `${baseUrl}/cash-list/config/${nursing_home_id}`,
    });

    this.getCashListTransactions = get({
      authContext,
      url: ({ cash_list_id, page, page_size, date_from, date_to }) => {
        const query = new URLSearchParams();
        let i = 0;

        if (page !== undefined) {
          query.append('page', page.toString());
        }

        if (page_size !== undefined) {
          query.append('page_size', page_size.toString());
        }

        // Add date filtering
        if (date_from) {
          query.append(`filters[${i}][field]`, 'creation_datetime');
          query.append(`filters[${i}][op]`, '>=');
          query.append(`filters[${i}][value]`, date_from.toISOString());
          i++;
        }

        if (date_to) {
          query.append(`filters[${i}][field]`, 'creation_datetime');
          query.append(`filters[${i}][op]`, '<=');
          query.append(`filters[${i}][value]`, date_to.toISOString());
          i++;
        }

        const queryString = query.toString();
        return `${baseUrl}/cash-list/${cash_list_id}/transactions${
          queryString ? `?${queryString}` : ''
        }`;
      },
    });

    this.getCashTransactionGroup = get({
      authContext,
      url: (params: { groupId: string }) =>
        `${baseUrl}/cash-list/cash-transaction-group/${params.groupId}`,
    });
  }
}
